import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spin, Tooltip, Button } from 'antd';

import { IGroupedShareCompareCar, IShareCompareCar, LocationsTypes } from '@share/common-types';
import { IMarginatorState, ILoginState, carsActions, marginatorActions, carsDatesActions, carsDriverActions, GetCars, carsLocationsActions } from '@share/store/slices';
import { formatDateCheckInOut, isSpanish, LoginType, renderNumber, UrlUtils, getAccountNumberPath } from '@share/utils';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { CARS_DATES_LABEL, CARS_DRIVER_LABEL, CARS_PICKUP_LOCATION_LABEL, CARS_PICKUP_LOCATION_OBJECT_LABEL, CARS_DROPOFF_LOCATION_LABEL, CARS_DROPOFF_LOCATION_OBJECT_LABEL, CARS_SESSION_KEY_LABEL } from '@share/constants';
import { Currency } from '@components';
import { ModalConfirmation } from '@components';
import { PeopleSvg, SuitecaseSvg, TransmitionSvg, AirSvg } from '@assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { HasPermission, ViewQuotesSharePermission } from '@utils';
import { get } from 'lodash';
import { BlueButton } from '@share/components';
import { es } from 'date-fns/locale';
import { SearchTypeEnum } from '@share/common-types';
import { Routes } from '@share/constants';
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";

interface ICarTabProps {
  cars: Record<string, IGroupedShareCompareCar[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;

  onCarRemove: (car: IShareCompareCar) => void;
  onCarGroupRemove: (cars: IShareCompareCar[]) => void;
  onCancel: () => void;
  handelDownload: () => void;
  handelShare: () => void;
}

const percents = 100;

export const CarTab: React.FC<ICarTabProps> = ({
  cars,
  marginatorStore,
  loginStore,

  onCarRemove,
  onCarGroupRemove,
  onCancel,
  handelDownload,
  handelShare,
}) => {
  const locations = Object.keys(cars);
  const [selectedLocation, setSelectedLocation] = useState(locations[0] || 'unknown_location');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const { account, user } = loginStore;
  const isB2C = account?.isB2C;
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [showRedirectConfirmation, setShowRedirectConfirmation] = useState(false);
  const [pendingCar, setPendingCar] = useState<IShareCompareCar | null>(null);

  const currentGroups = cars[selectedLocation] || [];

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  useEffect(() => {
    if (!selectedLocation || !cars[selectedLocation]) {
      const firstLoc = Object.keys(cars)[0];
      if (firstLoc) setSelectedLocation(firstLoc);
    }
  }, [cars]);

  const getSearchParamsFromCar = (car: IShareCompareCar) => {
    const startDate = car.startDate || '';
    const endDate = car.endDate || '';
    const startDateTime = car.carRequest?.pickUpTime || '';
    const endDateTime = car.carRequest?.returnTime || '';
    const driverAge = String(car.carRequest?.driverAge || '');
    const driverCountry = String(car.carRequest?.countryISO || '');
    const carSessionKey = car.sessionKey;

    const pickUpType = (car as any)?.carRequest?.pickUpType
      || (car.pickUpLocationDetail?.atAirport ? LocationsTypes.Airport : LocationsTypes.Destination);
    const returnType = (car as any)?.carRequest?.returnType
      || (car.dropOffLocationDetail?.atAirport ? LocationsTypes.Airport : LocationsTypes.Destination);

    const pickUpLocation = (car as any)?.pickUpLocationObj || {
      code: (car as any)?.carRequest?.pickUpLocation || car.pickUpLocationDetail?.locationCode,
      name: car.pickUpLocationDetail?.name || '',
      type: pickUpType,
      realType: pickUpType,
      geoLocation: car.pickUpLocationDetail?.position,
    };
    const dropOffLocation = (car as any)?.dropOffLocationObj || {
      code: (car as any)?.carRequest?.returnLocation || car.dropOffLocationDetail?.locationCode,
      name: car.dropOffLocationDetail?.name || '',
      type: returnType,
      realType: returnType,
      geoLocation: car.dropOffLocationDetail?.position,
    };

    return {
      startDate,
      endDate,
      startDateTime,
      endDateTime,
      driverAge,
      driverCountry,
      pickUpLocation,
      dropOffLocation,
      carSessionKey,
    };
  };

  const handleView = (car: IShareCompareCar, marginatorCommission: number) => {
    dispatch(marginatorActions.setCommission(marginatorCommission));
    dispatch(carsActions.setSelectedCar(car));

    const {
      startDate,
      endDate,
      startDateTime,
      endDateTime,
      driverAge,
      driverCountry,
      pickUpLocation,
      dropOffLocation,
      carSessionKey,
    } = getSearchParamsFromCar(car);

    const params = [
      { key: CARS_DATES_LABEL, value: { startDate, startDateTime, endDate, endDateTime } },
      { key: CARS_DRIVER_LABEL, value: { driverAge, driverCountry } },
      { key: CARS_PICKUP_LOCATION_OBJECT_LABEL, value: pickUpLocation },
      { key: CARS_PICKUP_LOCATION_LABEL, value: pickUpLocation.name },
      { key: CARS_DROPOFF_LOCATION_OBJECT_LABEL, value: dropOffLocation },
      { key: CARS_DROPOFF_LOCATION_LABEL, value: dropOffLocation.name },
      { key: CARS_SESSION_KEY_LABEL, value: carSessionKey },
    ];

    const queryString = UrlUtils.getQueryString(params);
    const accountPath = getAccountNumberPath(history);
    const Url = `${accountPath}${Routes.CarsDetails}/${car.id}?${queryString}`;
    window.open(Url, "_blank");
  };

  const onCarSearchPage = (car: IShareCompareCar) => {
    if (!car) return;
    const {
      startDate,
      endDate,
      startDateTime,
      endDateTime,
      driverAge,
      driverCountry,
      pickUpLocation,
      dropOffLocation,
      carSessionKey,
    } = getSearchParamsFromCar(car);

    UrlUtils.setUrl(CARS_DATES_LABEL, { startDate, startDateTime, endDate, endDateTime });
    UrlUtils.setUrl(CARS_DRIVER_LABEL, { driverAge, driverCountry });
    UrlUtils.setUrl(CARS_PICKUP_LOCATION_OBJECT_LABEL, pickUpLocation);
    UrlUtils.setUrl(CARS_PICKUP_LOCATION_LABEL, pickUpLocation.name);
    UrlUtils.setUrl(CARS_DROPOFF_LOCATION_OBJECT_LABEL, dropOffLocation);
    UrlUtils.setUrl(CARS_DROPOFF_LOCATION_LABEL, dropOffLocation.name);
    if (carSessionKey) {
      UrlUtils.setUrl(CARS_SESSION_KEY_LABEL, carSessionKey);
      dispatch(carsActions.setCarsSessionKey(carSessionKey));
    }

    dispatch(carsLocationsActions.setSelectPickUpLocation(pickUpLocation as any));
    dispatch(carsLocationsActions.setSelectPickUpLocationLabel(pickUpLocation.name));
    dispatch(carsLocationsActions.setSelectDropOffLocation(dropOffLocation as any));
    dispatch(carsLocationsActions.setSelectDropOffLocationLabel(dropOffLocation.name));
    dispatch(carsDatesActions.setDatesSelected({ startDate, endDate }));
    dispatch(carsDatesActions.setTimesSelected({ startDateTime, endDateTime }));
    dispatch(carsDriverActions.setDriverAgeSelected(driverAge));
    dispatch(carsDriverActions.setDriverCountrySelected(driverCountry));
    dispatch(GetCars(SearchTypeEnum.NewSearch));
    const currentPath = history.location.pathname.toLowerCase();
    const isCarsPage = currentPath.includes('/cars');
    onCancel?.();
    if (!isCarsPage) {
      const accountName = account?.name || '';
      history.push(`/${accountName}${Routes.CarsSearch}${window.location.search}`);
    }
  };

  const selectedCar = currentGroups[activeGroupIndex]?.items?.[0];
  const pickUpLocationName = (selectedCar as any)?.pickUpLocationObj?.name;
  const dropOffLocationName = (selectedCar as any)?.dropOffLocationObj?.name;
  const redirectMessage = 'You will redirect to search page with this specific date and detail. Are you sure?';

  const onClickMeta = () => {
    if (!selectedCar) return;
    setPendingCar(selectedCar);
    setShowRedirectConfirmation(true);
  };

  const onConfirmRedirect = () => {
    if (pendingCar) {
      onCarSearchPage(pendingCar);
    }
    setShowRedirectConfirmation(false);
    setPendingCar(null);
  };

  const onCancelRedirect = () => {
    setShowRedirectConfirmation(false);
    setPendingCar(null);
  };

  return (
    <div>
     {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locCode) => {
                  const firstItem = cars[locCode][0]?.items[0];
                  const pickUpName =
                    firstItem?.pickUpLocationDetail?.name || locCode;
                  const dropOffName = firstItem?.dropOffLocationDetail?.name;

                  const isDifferent = dropOffName && pickUpName !== dropOffName;

                  return (
                    <button
                      key={locCode}
                      className={`tab-btn ${
                        selectedLocation === locCode ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedLocation(locCode);
                        setActiveGroupIndex(0);
                      }}
                    >
                      {isDifferent
                        ? `${pickUpName} - ${dropOffName}`
                        : pickUpName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* DATE TABS */}
          {currentGroups.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => (
                  <button
                    key={index}
                    className={`tab-btn ${
                      activeGroupIndex === index ? "active" : ""
                    }`}
                    onClick={() => setActiveGroupIndex(index)}
                  >
                    {formatDateCheckInOut(
                      group.startDate,
                      DATE_FORMAT_CHECK_IN_OUT
                    )}{" "}
                    -{" "}
                    {formatDateCheckInOut(
                      group.endDate,
                      DATE_FORMAT_CHECK_IN_OUT
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {(pickUpLocationName || dropOffLocationName) ? (
        <div className='modal-compare__hotels-location'>
          {pickUpLocationName ? <div>Pickup: {pickUpLocationName}</div> : null}
          {dropOffLocationName ? <div>Drop-off: {dropOffLocationName}</div> : null}
        </div>
      ) : null}
      <div className="modal-compare__hotels modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <a className='modal-compare__hotels_link' onClick={onClickMeta}>
          <div className="hotels-dates">
            {formatDateCheckInOut(currentGroups[activeGroupIndex]?.startDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
            {formatDateCheckInOut(currentGroups[activeGroupIndex]?.endDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
          </div>
          <div className="hotels-dates text-start">
            <span className="compare-meta-item">
              <FormattedMessage id="cars.pickup.time" />: <strong>{currentGroups[activeGroupIndex]?.items[0]?.carRequest?.pickUpTime}</strong>
            </span>
            <span className="compare-meta-item">
              <FormattedMessage id="cars.dropoff.time" />: <strong>{currentGroups[activeGroupIndex]?.items[0]?.carRequest?.returnTime}</strong>
            </span>
            <span className="compare-meta-item">
              <FormattedMessage id="cars.driver.age" />: <strong>{currentGroups[activeGroupIndex]?.items[0]?.carRequest?.driverAge}</strong>
            </span>
            <span className="compare-meta-item">
              <FormattedMessage id="country" />: <strong>{currentGroups[activeGroupIndex]?.items[0]?.carRequest?.countryISO}</strong>
            </span>
          </div>
        </a>
        {currentGroups.length > 1 && (
          <Button
            danger
            onClick={() => onCarGroupRemove(currentGroups[activeGroupIndex]?.items)}
          >
            <FormattedMessage id="results.hotel.card.compare.modal.remove_quote" defaultMessage="Remove Quote" />
          </Button>
        )}
      </div>

      <div className="modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((car, index) => {
          const { price } = car;
          const { value } = marginatorStore;
          const marginatorPrice = price / (1 - value / 100);
          const marginatorCommission = marginatorPrice - price;

          const publicPrice =
            isB2C || !car.publicPrices?.length ? null : Math.min(...car.publicPrices.map((p) => p.price));

          const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
          const savings = Math.floor(percents - msave);
          const logoUrl = car?.info?.tpA_Extensions?.supplierLogo?.url || '';

          return (
            <div key={index} className="modal-compare__hotel">
              <div className="modal-compare__media">
                <div className="hotel-images-container hotelImageHeight">
                  <div
                    className="hotel-image"
                    style={{
                      backgroundImage: `url(${car.vehicle?.pictureURL || get(car, 'images[0].url')})`,
                    }}
                  />
                  {savings > 0 ? (
                    <div className="modal-compare__savings-badge">
                      {savings.toFixed(0)} % <FormattedMessage id="results.cars.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
                <label
                  className={`modal-compare__remove-btn ${currentGroups[activeGroupIndex]?.items.length <= 2 ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentGroups[activeGroupIndex]?.items.length > 2) {
                      onCarRemove(car);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCircleXmark} className="modal-compare__remove-btn-icon" />
                </label>
              </div>

              <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                <p
                  className="mb-0"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    lineClamp: 1,
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {logoUrl ? (
                    <div className="cars-content-location__vendor">
                      <img alt="Vendor logo" src={logoUrl} style={{ maxWidth: '100%', height: 'auto' }} />
                    </div>
                  ) : (
                    <span>{car?.vehicle?.vehMakeModel?.name || '-'}</span>
                  )}
                </p>
              </div>
              <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                <p
                  className="mb-0"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    lineClamp: 1,
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  <Tooltip title={car.vehicle?.vehMakeModel?.name} placement="top">
                    {car.vehicle?.vehMakeModel?.name}
                  </Tooltip>
                </p>
              </div>

              <div className="hotel-text-container hotel-text-container--car-location">
                <Tooltip
                  title={`${car.vendor?.value} ${car.pickUpLocationDetail?.name}`}
                  placement="top"
                >
                  <p>
                    {car.vendor?.value} {car.pickUpLocationDetail?.name}
                  </p>
                </Tooltip>
              </div>

              <div className="hotel-text-container" style={{ minHeight: '50px', height: 'auto' }}>
                <div className="cars-card__item">
                  <TransmitionSvg />
                  <label className="ms-1">{car.vehicle?.transmissionType}</label>
                </div>
                <div className="cars-card__item">
                  <PeopleSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.filter.seats" />: {car.vehicle?.passengerQuantity}
                  </label>
                </div>
                <div className="cars-card__item">
                  <SuitecaseSvg />
                  <label className="ms-1">
                    <FormattedMessage id="adult" values={{ count: car.vehicle?.passengerQuantity }} />:{' '}
                    {car.vehicle?.passengerQuantity}
                  </label>
                </div>
                <div className="cars-card__item">
                  <SuitecaseSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.baggage" />: {car.vehicle?.baggageQuantity}
                  </label>
                </div>
                <div className="cars-card__item">
                  <AirSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.ac" />
                  </label>
                </div>
              </div>

              <div className="hotel-text-container hotel-text-container--refundable">
                <div className="hotel-card-wrapper__refundable">
                  {car.refundable ? (
                    <div className="hotel-card-wrapper__refundable-ref">
                      <FormattedMessage id="refundable" />
                    </div>
                  ) : (
                    <div className="hotel-card-wrapper__refundable-nonref">
                      <FormattedMessage id="non.refundable" />
                    </div>
                  )}
                </div>
              </div>
              <div className="hotel-text-container hotel-text-container--price">
                {publicPrice ? (
                  <p className="mb-0" style={{ textDecoration: 'line-through' }}>
                    <Currency currency={car.currency} />
                    {renderNumber(publicPrice, 2)}
                  </p>
                ) : null}
                <p className="mb-0">
                  <strong>
                    <Currency currency={car.currency} />
                    {renderNumber(marginatorPrice, 2)}
                  </strong>
                </p>
              </div>

              <div className="hotel-view-container">
                <div className="view-button" onClick={() => handleView(car, marginatorCommission)}>
                  <FormattedMessage id="results.cars.card.compare.modal.view" />
                </div>
              </div>
            </div>

          )
        })}
      </div>

      <div className="modal-compare__buttons">
        {!account || account.type !== LoginType.PrivateWithToken ? (
          account?.displayShareCompareCars && (
            <BlueButton disabled={loading} onClick={handelDownload}>
              {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.download" />}
            </BlueButton>
          )
        ) : (
          <>
            {account?.displayShareCompareCars && (
              <BlueButton
                disabled={loading}
                onClick={handelDownload}
              >
                {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.download" />}
              </BlueButton>
            )}

            {HasPermission(user, ViewQuotesSharePermission) ? (
              <BlueButton
                disabled={loading}
                onClick={() => { handelShare() }}
              >
                {loading ? <Spin /> : <FormattedMessage id="results.cars.card.compare.modal.share" />}
              </BlueButton>
            ) : null}
          </>
        )}
      </div>
      <ModalConfirmation
        className="modal-share"
        wrapClassName="modal-share__confirmation-wrapper"
        title="results.hotel.card.compare.modal.share"
        message={redirectMessage}
        yesMessage="share.continue"
        noMessage="share.cancel"
        visible={showRedirectConfirmation}
        disableMessageLocalization
        onCancel={onCancelRedirect}
        onConfirm={onConfirmRedirect}
      />
    </div>
  );
};
